import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ExternalLink,
  Calendar,
  MapPin,
  Tag,
  Share2,
  Copy,
  Facebook,
  Linkedin,
  Twitter,
  MessageCircle,
  Send,
  EyeOff,
  Trash2,
  Loader2,
  Check,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DOMPurify from "dompurify";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/**
 * Full-content viewer + share + comments modal for published posts.
 * Larger reading experience: max-w-4xl, comfortable line-height, native sharing.
 */
export default function ContentDetailModal({ open, onClose, item, businessName, businessOrgId }) {
  const { user, getAuthHeader, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [commentForm, setCommentForm] = useState({ name: "", email: "", body: "" });
  const [replyOpen, setReplyOpen] = useState({});  // {commentId: true}
  const [replyText, setReplyText] = useState({});  // {commentId: "text"}
  const [copied, setCopied] = useState(false);

  const isAuthor = !!(isAuthenticated && user && businessOrgId && user.organization_id === businessOrgId);
  const shareUrl = useMemo(() => (typeof window !== "undefined" ? window.location.href : ""), [open]);
  const shareTitle = item?.title || "Check out this post";

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Load comments
  useEffect(() => {
    if (!open || !item?.content_id) return;
    setLoadingComments(true);
    axios.get(`${API_V1}/content/${item.content_id}/comments`)
      .then((r) => setComments(r.data?.comments || []))
      .catch(() => setComments([]))
      .finally(() => setLoadingComments(false));
  }, [open, item?.content_id]);

  if (!item) return null;

  const ytMatch = (item.video_url || item.content_url || "").match(/(?:watch\?v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]+)/);
  const videoId = ytMatch?.[1];
  const img = item.image_url || item.thumbnail_url || item.featured_image;
  const body = item.body || item.content || item.description || "";
  const externalLink = item.external_url || item.content_url || item.ticket_url;
  const typeLabel = (item.dashboard_type || item.content_type || "post").replace(/_/g, " ");

  // === Sharing ===
  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: item.title, url: shareUrl });
      } else {
        copyLink();
      }
    } catch (_) { /* user cancelled */ }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { toast.error("Could not copy link"); }
  };
  const shareTo = (network) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareTitle);
    let url = "";
    if (network === "facebook") url = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    else if (network === "twitter") url = `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    else if (network === "linkedin") url = `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    if (url) window.open(url, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  // === Comments ===
  const postComment = async (e) => {
    e?.preventDefault();
    if (!commentForm.name.trim() || !commentForm.email.trim() || !commentForm.body.trim()) {
      toast.error("Name, email and a comment are required");
      return;
    }
    setPostingComment(true);
    try {
      const r = await axios.post(`${API_V1}/content/${item.content_id}/comments`, commentForm);
      setComments((c) => [...c, { ...r.data, replies: [] }]);
      setCommentForm((p) => ({ ...p, body: "" }));
      toast.success("Comment posted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post comment");
    } finally {
      setPostingComment(false);
    }
  };

  const submitReply = async (commentId) => {
    const text = (replyText[commentId] || "").trim();
    if (!text) return;
    try {
      const r = await axios.post(
        `${API_V1}/content/${item.content_id}/comments/${commentId}/reply`,
        { body: text },
        { headers: getAuthHeader() }
      );
      // Insert reply into the parent
      setComments((cs) =>
        cs.map((c) =>
          c.id === commentId ? { ...c, replies: [...(c.replies || []), r.data] } : c
        )
      );
      setReplyText((p) => ({ ...p, [commentId]: "" }));
      setReplyOpen((p) => ({ ...p, [commentId]: false }));
      toast.success("Reply posted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post reply");
    }
  };

  const hideComment = async (commentId) => {
    try {
      await axios.patch(`${API_V1}/content/comments/${commentId}/hide`, {}, { headers: getAuthHeader() });
      setComments((cs) =>
        cs.filter((c) => c.id !== commentId).map((c) => ({
          ...c,
          replies: (c.replies || []).filter((r) => r.id !== commentId),
        }))
      );
      toast.success("Comment hidden");
    } catch (err) {
      toast.error("Could not hide comment");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-0 sm:p-6 bg-black/85 backdrop-blur-sm overflow-y-auto"
          onClick={onClose}
          data-testid="content-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            className="relative w-full max-w-4xl sm:max-h-[92vh] sm:rounded-2xl overflow-hidden bg-[#0B1226] border border-white/10 shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 sticky top-0 bg-[#0B1226]/95 backdrop-blur z-10">
              <div className="flex items-center gap-2 min-w-0">
                <Badge className="bg-[#7C3AED]/20 text-[#7C3AED] capitalize text-xs">{typeLabel}</Badge>
                {businessName && (
                  <span className="text-xs text-[#94A3B8] truncate">by {businessName}</span>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-white/10 transition"
                aria-label="Close"
                data-testid="content-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto px-4 sm:px-8 py-6 sm:py-8 max-h-[calc(92vh-3.5rem)]">
              <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] text-white mb-6 leading-tight" data-testid="content-modal-title">
                {item.title}
              </h2>

              {/* Share row */}
              <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="share-row">
                <span className="text-xs uppercase tracking-wider text-[#64748B] mr-1">Share</span>
                <button onClick={handleNativeShare} className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm flex items-center gap-1.5 transition" data-testid="share-native-btn">
                  <Share2 className="w-3.5 h-3.5" /> Share
                </button>
                <button onClick={copyLink} className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm flex items-center gap-1.5 transition" data-testid="share-copy-btn">
                  {copied ? <><Check className="w-3.5 h-3.5 text-[#10B981]" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
                </button>
                <button onClick={() => shareTo("facebook")} className="w-8 h-8 rounded-full bg-[#1877F2]/15 hover:bg-[#1877F2]/30 text-[#1877F2] flex items-center justify-center transition" aria-label="Share on Facebook" data-testid="share-facebook-btn"><Facebook className="w-4 h-4" /></button>
                <button onClick={() => shareTo("twitter")} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition" aria-label="Share on X" data-testid="share-twitter-btn"><Twitter className="w-4 h-4" /></button>
                <button onClick={() => shareTo("linkedin")} className="w-8 h-8 rounded-full bg-[#0A66C2]/15 hover:bg-[#0A66C2]/30 text-[#0A66C2] flex items-center justify-center transition" aria-label="Share on LinkedIn" data-testid="share-linkedin-btn"><Linkedin className="w-4 h-4" /></button>
              </div>

              {/* Media */}
              {videoId ? (
                <div className="aspect-video w-full rounded-xl overflow-hidden bg-black mb-6">
                  <iframe
                    title={item.title}
                    src={`https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1`}
                    className="w-full h-full"
                    style={{ border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : item.video_url ? (
                <video
                  src={item.video_url}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[70vh] rounded-xl object-contain bg-black mb-6"
                  data-testid="content-modal-video"
                />
              ) : img ? (
                <img
                  src={img}
                  alt={item.title}
                  className="w-full max-h-[70vh] object-contain rounded-xl bg-black/30 mb-6"
                  data-testid="content-modal-image"
                />
              ) : null}

              {/* Type-specific meta */}
              {(item.salary || item.company_name || item.job_location) && (
                <div className="flex flex-wrap gap-3 mb-5 text-sm text-[#94A3B8]">
                  {item.company_name && <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> {item.company_name}</span>}
                  {item.job_location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {item.job_location}</span>}
                  {item.salary && <span className="text-[#10B981] font-semibold">{item.salary}</span>}
                </div>
              )}
              {(item.event_date || item.venue) && (
                <div className="flex flex-wrap gap-3 mb-5 text-sm text-[#94A3B8]">
                  {item.event_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {item.event_date}</span>}
                  {item.venue && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {item.venue}</span>}
                </div>
              )}
              {(item.discount_amount || item.coupon_code) && (
                <div className="rounded-lg bg-[#10B981]/10 border border-[#10B981]/30 p-3 mb-5">
                  {item.discount_amount && <p className="text-[#10B981] font-bold text-lg">{item.discount_amount} OFF</p>}
                  {item.coupon_code && <p className="text-sm text-white">Code: <span className="font-mono font-semibold">{item.coupon_code}</span></p>}
                  {item.expiry_date && <p className="text-xs text-[#94A3B8] mt-1">Expires: {item.expiry_date}</p>}
                </div>
              )}

              {/* Body — large readable typography */}
              {body && (
                /<\/?[a-z][\s\S]*>/i.test(body) ? (
                  <div
                    className="prose prose-invert prose-lg max-w-none text-[#E2E8F0] leading-[1.75] prose-a:text-[#00E5FF] prose-a:no-underline hover:prose-a:underline"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body, { ADD_ATTR: ["target", "rel"] }) }}
                    data-testid="content-modal-body"
                  />
                ) : (
                  <div className="text-[#E2E8F0] text-base sm:text-lg whitespace-pre-wrap leading-[1.75]" data-testid="content-modal-body">
                    {body}
                  </div>
                )
              )}

              {/* External link / Read full article CTA */}
              {externalLink && (
                <a
                  href={externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white font-semibold hover:opacity-90 transition"
                  data-testid="content-modal-external-link"
                >
                  Read full article <ExternalLink className="w-4 h-4" />
                </a>
              )}

              {/* === COMMENTS === */}
              <div className="mt-10 pt-8 border-t border-white/10" data-testid="comments-section">
                <h3 className="text-xl font-bold text-white mb-5 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#00E5FF]" />
                  Comments {comments.length > 0 && <span className="text-sm text-[#94A3B8] font-normal">({comments.length})</span>}
                </h3>

                {/* Post form */}
                <form onSubmit={postComment} className="rounded-xl bg-white/5 border border-white/10 p-4 mb-6" data-testid="comment-form">
                  <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <Input
                      placeholder="Your name"
                      value={commentForm.name}
                      onChange={(e) => setCommentForm((p) => ({ ...p, name: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="comment-name-input"
                    />
                    <Input
                      type="email"
                      placeholder="Your email (kept private)"
                      value={commentForm.email}
                      onChange={(e) => setCommentForm((p) => ({ ...p, email: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="comment-email-input"
                    />
                  </div>
                  <textarea
                    placeholder="Share a thought…"
                    value={commentForm.body}
                    onChange={(e) => setCommentForm((p) => ({ ...p, body: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#00E5FF]"
                    data-testid="comment-body-input"
                  />
                  <div className="flex justify-end mt-3">
                    <Button type="submit" disabled={postingComment} className="bg-[#00E5FF] hover:bg-[#22D3EE] text-[#0B1226] font-semibold" data-testid="comment-submit-btn">
                      {postingComment ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Post Comment
                    </Button>
                  </div>
                </form>

                {/* Comments list */}
                {loadingComments ? (
                  <div className="text-center py-6 text-[#64748B] text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading comments…</div>
                ) : comments.length === 0 ? (
                  <p className="text-center py-6 text-[#64748B] text-sm">Be the first to comment.</p>
                ) : (
                  <ul className="space-y-5">
                    {comments.map((c) => (
                      <li key={c.id} className="" data-testid={`comment-${c.id}`}>
                        <div className="flex gap-3">
                          <img src={c.avatar_url} alt={c.name} className="w-9 h-9 rounded-full bg-white/10 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-white font-semibold text-sm">{c.name}</span>
                              {c.is_author_reply && <Badge className="bg-[#10B981]/20 text-[#10B981] text-[10px] px-1.5 py-0">Author</Badge>}
                              <span className="text-[11px] text-[#64748B]">{formatTime(c.created_at)}</span>
                            </div>
                            <p className="text-[#E2E8F0] text-sm whitespace-pre-wrap leading-relaxed">{c.body}</p>
                            <div className="flex items-center gap-3 mt-2">
                              {isAuthor && (
                                <>
                                  <button onClick={() => setReplyOpen((p) => ({ ...p, [c.id]: !p[c.id] }))} className="text-xs text-[#00E5FF] hover:underline" data-testid={`reply-btn-${c.id}`}>
                                    {replyOpen[c.id] ? "Cancel" : "Reply"}
                                  </button>
                                  <button onClick={() => hideComment(c.id)} className="text-xs text-[#94A3B8] hover:text-white inline-flex items-center gap-1" data-testid={`hide-btn-${c.id}`}>
                                    <EyeOff className="w-3 h-3" /> Hide
                                  </button>
                                </>
                              )}
                            </div>

                            {/* Reply box */}
                            {isAuthor && replyOpen[c.id] && (
                              <div className="mt-3 flex gap-2" data-testid={`reply-box-${c.id}`}>
                                <Input
                                  placeholder={`Reply as ${businessName || "the author"}…`}
                                  value={replyText[c.id] || ""}
                                  onChange={(e) => setReplyText((p) => ({ ...p, [c.id]: e.target.value }))}
                                  className="bg-white/5 border-white/10 text-white text-sm"
                                />
                                <Button onClick={() => submitReply(c.id)} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid={`reply-submit-${c.id}`}>
                                  <Send className="w-4 h-4" />
                                </Button>
                              </div>
                            )}

                            {/* Replies */}
                            {(c.replies || []).length > 0 && (
                              <ul className="mt-4 pl-4 border-l border-white/10 space-y-4">
                                {c.replies.map((r) => (
                                  <li key={r.id} className="flex gap-3" data-testid={`reply-${r.id}`}>
                                    <img src={r.avatar_url} alt={r.name} className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className="text-white font-semibold text-sm">{r.name}</span>
                                        {r.is_author_reply && <Badge className="bg-[#10B981]/20 text-[#10B981] text-[10px] px-1.5 py-0">Author</Badge>}
                                        <span className="text-[11px] text-[#64748B]">{formatTime(r.created_at)}</span>
                                      </div>
                                      <p className="text-[#E2E8F0] text-sm whitespace-pre-wrap leading-relaxed">{r.body}</p>
                                      {isAuthor && (
                                        <button onClick={() => hideComment(r.id)} className="text-xs text-[#94A3B8] hover:text-white inline-flex items-center gap-1 mt-1">
                                          <Trash2 className="w-3 h-3" /> Remove
                                        </button>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function formatTime(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch (_) { return ""; }
}
